import { useEffect, useState, useCallback, useRef } from "react";
import { PlanUsage } from "@/components/PlanUsage";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { brl, num, locString } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { PartForm } from "@/components/PartForm";
import { BatchQRDialog } from "@/components/BatchQRDialog";
import { ImportProducts } from "@/components/ImportProducts";
import { ZoomableImage } from "@/components/ZoomableImage";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ForceDeleteDialog } from "@/components/ForceDeleteDialog";
import { Search, Plus, Package, MapPin, Cog, Boxes, QrCode, Trash2, Upload } from "lucide-react";

export default function Parts() {
  const { can, hasModule } = useAuth();
  const isAdmin = can();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [stockStatus, setStockStatus] = useState("");
  const requestRef = useRef(0);
  const [parts, setParts] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [meta, setMeta] = useState({ total: 0, total_pages: 1 });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [batchOpen, setBatchOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const load = useCallback((term, pg, size, situation) => {
    const request = ++requestRef.current;
    setParts(null);
    const params = { q: term || undefined, page: pg, page_size: size, stock_status: situation || undefined };
    api.get("/parts", { params })
      .then((r) => {
        if (request !== requestRef.current) return;
        const totalPages = Math.max(1, r.data.total_pages);
        if (pg > totalPages) {
          setPage(totalPages);
          return;
        }
        setParts(r.data.items);
        setMeta({ total: r.data.total, total_pages: totalPages });
      })
      .catch(() => {
        if (request !== requestRef.current) return;
        setParts([]); setMeta({ total: 0, total_pages: 1 });
      });
  }, []);

  useEffect(() => { load(debouncedQuery, page, pageSize, stockStatus); }, [load, debouncedQuery, page, pageSize, stockStatus]);
  useEffect(() => {
    const timer = setTimeout(() => { setPage(1); setDebouncedQuery(q); }, 350);
    return () => clearTimeout(timer);
  }, [q]);
  useEffect(() => () => { requestRef.current += 1; }, []);
  const reload = () => load(debouncedQuery, page, pageSize, stockStatus);

  const [del, setDel] = useState(null);
  const [delBusy, setDelBusy] = useState(false);
  const openDelete = async (e, p) => {
    e.stopPropagation();
    try {
      const { data } = await api.get(`/parts/${p.id}/has-history`);
      setDel({ id: p.id, name: p.descricao, hasHistory: data.has_history });
    } catch (err) { toast.error(apiErr(err)); }
  };
  const confirmDelete = async () => {
    setDelBusy(true);
    try {
      await api.delete(`/parts/${del.id}/permanent`, { params: del.hasHistory ? { force: true } : {} });
      toast.success("Peça excluída definitivamente.");
      setDel(null); reload();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setDelBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-50">Peças</h1>
          <p className="text-slate-400 text-sm">Busca inteligente por descrição, código, máquina, categoria ou fornecedor.</p>
        </div>
        <PlanUsage kind="pecas" refreshKey={meta} />
        {can("ALMOXARIFADO") && (
          <div className="flex flex-wrap gap-2">
            {hasModule("importacoes") && (
              <Button data-testid="btn-import-products" variant="secondary" onClick={() => setImportOpen(true)} className="bg-emerald-900/40 hover:bg-emerald-800/50 text-emerald-100">
                <Upload className="h-4 w-4 mr-1" /> Importar produtos
              </Button>
            )}
            <Button data-testid="btn-batch-qr" variant="secondary" onClick={() => setBatchOpen(true)}>
              <QrCode className="h-4 w-4 mr-1" /> Etiquetas QR
            </Button>
            <Button data-testid="btn-new-part" onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-sky-700 hover:bg-sky-600">
              <Plus className="h-4 w-4 mr-1" /> Nova peça
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
      <div className="relative flex-1 max-w-2xl">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
        <Input
          data-testid="input-search-part"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder='Ex.: "resistência cabeçote extrusora 160"'
          className="pl-11 h-12 bg-slate-900/70 border-slate-700 text-base"
        />
      </div>

      <div className="w-full sm:w-52 shrink-0">
        <label htmlFor="parts-stock-status" className="block text-xs text-slate-300 mb-1">Situação de estoque</label>
        <select id="parts-stock-status" data-testid="parts-stock-status" value={stockStatus}
          onChange={(e) => { setStockStatus(e.target.value); setPage(1); }}
          className="w-full h-12 rounded-md bg-slate-900 border border-slate-700 px-3 text-slate-100">
          <option value="">Todas</option>
          <option value="ADEQUADO">Normal</option>
          <option value="ATENCAO">Estoque baixo</option>
          <option value="CRITICO">Crítico</option>
          <option value="SEM_ESTOQUE">Sem estoque</option>
          <option value="EXCESSO">Excesso</option>
        </select>
      </div>
      </div>

      {parts === null ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-40 bg-slate-800" />)}</div>
      ) : parts.length === 0 ? (
        <div className="text-center py-20 text-slate-500"><Package className="h-12 w-12 mx-auto mb-3 opacity-50" />Nenhuma peça encontrada.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {parts.map((p) => (
            <Card
              key={p.id}
              data-testid={`part-card-${p.id}`}
              onClick={() => navigate(`/pecas/${p.id}`)}
              className="card-metal p-0 overflow-hidden cursor-pointer group animate-in-up relative"
            >
              {isAdmin && (
                <button
                  data-testid={`btn-delete-part-${p.id}`}
                  onClick={(e) => openDelete(e, p)}
                  title="Excluir definitivamente"
                  className="absolute top-2 right-2 z-10 rounded-md bg-slate-950/70 p-1.5 text-rose-400 hover:text-rose-300 hover:bg-rose-950/60 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
              <div className="flex">
                <div className="w-28 shrink-0 bg-slate-950 overflow-hidden">
                  {p.foto_principal ? (
                    <ZoomableImage src={p.foto_principal} alt={p.descricao} testid={`part-photo-${p.id}`} className="h-full w-full object-cover group-hover:scale-105 transition-transform" />
                  ) : <div className="h-full flex items-center justify-center"><Package className="h-8 w-8 text-slate-700" /></div>}
                </div>
                <div className="flex-1 p-3 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-100 truncate">{p.descricao}</div>
                      <div className="font-mono text-xs text-sky-400">{p.codigo_interno}</div>
                    </div>
                    <StatusBadge status={p.metrics?.status_reposicao} />
                  </div>
                  <div className="mt-2 flex items-center gap-3 text-xs text-slate-400">
                    <span className="flex items-center gap-1"><Boxes className="h-3.5 w-3.5" /> <b className="text-slate-200">{num(p.quantidade_atual)}</b> {p.unidade}</span>
                    {p.custo_medio != null && <span className="text-slate-300 font-mono">{brl(p.custo_medio)}</span>}
                  </div>
                  <div className="mt-1 flex items-center gap-1 text-xs text-slate-500 truncate"><MapPin className="h-3.5 w-3.5 shrink-0" /> {locString(p.localizacao)}</div>
                  {p.maquinas?.length > 0 && (
                    <div className="mt-1 flex items-center gap-1 text-xs text-slate-500 truncate"><Cog className="h-3.5 w-3.5 shrink-0" /> {p.maquinas.map((m) => m.nome).join(", ")}</div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {parts !== null && meta.total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 pt-2" data-testid="parts-pagination">
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <span data-testid="parts-total">{meta.total} peça(s)</span>
            <span className="text-slate-600">·</span>
            <span>Por página:</span>
            <select
              data-testid="parts-page-size"
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-slate-100"
            >
              {[50, 100, 200].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="parts-prev" variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Anterior</Button>
            <span className="text-sm text-slate-300 font-mono" data-testid="parts-page-info">{page} / {meta.total_pages}</span>
            <Button data-testid="parts-next" variant="secondary" size="sm" disabled={page >= meta.total_pages} onClick={() => setPage((p) => Math.min(meta.total_pages, p + 1))}>Próxima</Button>
          </div>
        </div>
      )}

      <PartForm open={formOpen} onOpenChange={setFormOpen} part={editing} onSaved={reload} />
      <BatchQRDialog open={batchOpen} onOpenChange={setBatchOpen} />
      <ImportProducts open={importOpen} onOpenChange={setImportOpen} onDone={reload} />
      <ForceDeleteDialog open={!!del} onOpenChange={(v) => !v && setDel(null)} entityLabel="a peça" name={del?.name} hasHistory={del?.hasHistory} busy={delBusy} onConfirm={confirmDelete} />
    </div>
  );
}
